import Vue from 'vue';
import Component from 'vue-class-component';
import {Emit, Prop, Ref, Watch} from 'vue-property-decorator';
import { Status } from '@/models/status.model';
import { PRESENTATION_COURSE_TAB_EVENT, PRESENTATION_MODE_EVENTS } from '@/content/values/ed-presentation-mode.values';
import { TRANSPARENCY_EVENTS } from '@/core/values/ed-transparency-meeting.values';
import { MEETING_EVENTS } from '@/meetings/values/meetings.values';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';
import { COURSE_EVENTS } from '@/content/values/events';
import ToolsService from '@/providers/tools';
import { SECTION_TYPE_CODES } from '@/core/values/ed-transparency-meeting.values';
import {BLANK_PIXEL_IMG, BOARD_EVENTS} from '@/values/ed-whiteboard.values';
import BoardPage from '@/homework/models/board-page.model';
import QueuedExecutorService from '@/services/queued-executor.service';

@Component({
  name: 'ed-pdfjs'
})
export default class EdPdfViewer extends Vue {

  /* Props */
  @Prop() readonly url: string;
  @Prop({default: null}) readonly initNumberPage: number;
  @Prop({default: false, required: false}) isPresentationFile : boolean;
  @Prop({default: 'pdf-viewer', required: false}) readonly viewerIdPrefix : number;
  @Prop() readonly documentHash : string;
  @Prop() documentIndex : number;
  @Prop() courseId : number;
  @Prop({default: false, required: false}) readonly allowExtraPages: boolean;
  @Ref() readonly pdfViewerElement: any;

  /* Data */
  private readonly PAGE_LOT_TO_LOAD:number = 15;
  private documentPDF: any = {};
  private pages: any[] = [];
  private initPages: any[] = [];
  private numberPages: number = -1;
  private numberRenderPages: number = 15;
  private addPages: number = 15;
  private canvasViewport: number = 1000;
  private canvasHeight: number = 0;
  private canvasWidth: number = 0;
  private flagRender: boolean = true;
  private initPage: number = null;
  private isResized: boolean = false;
  public addPendingPages: number = 0;
  public pageSize: number = 0;
  public pdfStatus: Status = new Status();
  private totalHeight : number = 0;
  private currentVisiblePage : Element = null;
  private pdfClientHeightHalf : number = 0;
  private homeworksSideBar : boolean = true;
  private currentPageClass : string = 'pdf-container__current-page';
  private currentPageTeacherClass : string = 'pdf-container__current-page__teacher';
  private canvasClass : string = '.pdf-container__canvas';
  private pdfDocLoadingTask: any;
  private currentSelectedPage: number = 1; // Current selected page number, starting from 1
  private pageRenderingQueue = new QueuedExecutorService();

  /* Computed */
  public get idRender(): string {
    let id: string = String(new Date().getTime());
    return `${this.documentHash}${id}`;
  }

  get pdfID(): string {
    return `EdPdfViewer${this.idRender}`;
  }

  get pdfViewer(): string {
    return `${this.viewerIdPrefix}${this.idRender}`;
  }

  get classes() {
    return {
      'ed-file-presentation': this.isPresentationFile //Only to find the element
    }
  }

  get zoomObject(): boolean {
    return this.$store.state.CourseStore.zoomObject;
  }

  get isPresentationMode() : boolean{
    return this.$store.state.CourseStore.isPresentationStudentMode || this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get isPresentationTeacherMode() : boolean{
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get courseSelected(): number {
    return this.$store.state.CourseStore.presentationCourseSelected;
  }

  get currentBoardUserId() : number {
    return this.$store.state.MeetingsStore.currentBoardUser.id;
  }

  get userId() : number {
    return this.$store.state.AuthStore.user.id;
  }

  get isSameUser() : boolean {
    return this.currentBoardUserId === this.userId;
  }

  get isBoardActive() : boolean {
    return this.$store.state.HomeworksStore.isBoardActive;
  }

  get isWhiteboardTransparency() {
    return this.$store.state.MeetingsStore.isWhiteboardTransparency;
  }

  get isPresentationModeTaskBoard() {
    return document.querySelectorAll('.ed-presentation-mode-task')[0] !== undefined;
  }

  private async setPdf(url: string) {
      const pdfDocLoadingTask  = pdfjsLib.getDocument({ url, stopAtErrors: true, withCredentials: true });
      this.pdfDocLoadingTask = pdfDocLoadingTask;
      pdfDocLoadingTask.promise.then(async (pdfDocProxy: any) => {
        this.documentPDF = pdfDocProxy;
        this.numberPages = pdfDocProxy._pdfInfo.numPages;
        for (let i = 1; i <= this.numberPages; i++) {
          let page =  await this.documentPDF.getPage(i);
          this.obtainSize(page, i);
        }
        this.recalculatePageSizes();

        let initPages = this.setInitPagesInterval();
        await this.getPagesDown({
          startPage: initPages.startPage,
          endPage  : initPages.endPage
        });
        await this.initRender();
      });

      return pdfDocLoadingTask;
  }

  private obtainSize(page : any, index : number) : void {
    let initialViewport = page.getViewport({ scale: 1 });
    let scale : number = (this.canvasViewport / initialViewport.width);
    let viewport: any = page.getViewport({ scale });
    this.totalHeight += viewport.height;
    this.initPages.push({
      id: index,
      height: viewport.height,
      width: viewport.width,
      initPage: this.totalHeight
    });
  }


  private async initRender() {
    this.onInitRender();
    if(this.pdfViewerElement) {
      let initPagesInterval = this.setInitPagesInterval();
      let pdfClientHeight = this.pdfViewerElement.clientHeight;
      this.pdfClientHeightHalf = pdfClientHeight / 2;

      for(let i = 0 ; i < this.pages.length ; i++) {
        let pdfId: string = String(initPagesInterval.startPage + i) + this.idRender;
        this.pageRenderingQueue.add(async () => await this.renderPage(this.pages[i], pdfId));
      }
      this.numberRenderPages = initPagesInterval.endPage + 1;
      this.pdfStatus.setLoad();
      this.setCanvasLeft();
      let containerToSelect: HTMLElement = this.pdfViewerElement;

      this.defineCurrentPage(0);

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          this.initPage--;
  
          if(!this.zoomObject){
            this.changeCanvasSize();
          }
          this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
          this.$Courses.Store.setPdfLoading(false);
          this.$edEventBus.$emit(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash), false);
          this.updateCanvasHeight();
          resolve('done');
        },0);
      });
    }
  }


  private findCurrentPage(scrollTop: number) {
    const pageIndex = this.calculateCurrentPage(scrollTop);
    if (pageIndex !== null) {
      this.defineCurrentPage(pageIndex);
    }
  }

  private calculateCurrentPage(scrollTop: number): number | null {
    // If the user is at the top of the page, we set the current page to 0
    if (scrollTop === 0) {
      return 0;
    }
    // And if the user is at the bottom of the page, we set the current page to the last page
    if (scrollTop >= this.pdfViewerElement.scrollHeight - this.pdfViewerElement.clientHeight) {
      return this.initPages.length - 1;
    }

    const clientHeightHalf = this.pdfViewerElement.clientHeight / 2;
    let currentPageInfo: any;
    if (!this.zoomObject && !this.isPresentationModeTaskBoard) {
      currentPageInfo = this.initPages
        .find(page => scrollTop >= (page.zoomInitPage + 2) - clientHeightHalf
          && scrollTop < (page.zoomHeight + page.zoomInitPage) - clientHeightHalf);
    } else {
      currentPageInfo = this.initPages
        .find(page => scrollTop >= (page.initPage + 2) - clientHeightHalf
          && scrollTop < (page.height + page.initPage) - clientHeightHalf);
    }

    return currentPageInfo ? currentPageInfo.id - 1 : null;
  }

  private defineCurrentPage(index: number) {
    this.setCurrentPageState(index);
    this.updateCurrentPageClasses(index);
  }

  private setCurrentPageState(index: number) {
    if (this.currentSelectedPage != index + 1) {
      this.currentSelectedPage = index + 1;
      this.onChangeSelectedPage(this.currentSelectedPage);
    }
    const page = this.initPages[index];
    const data: any = this.zoomObject
                    ? {initSize: page.initPage, endSize: (page.height + page.initPage), page: page.id}
                    : {initSize: page.zoomInitPage, endSize: (page.zoomHeight + page.initPage), page: page.id};
    this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_CURRENT_PAGE_SIZES, { key: this.documentIndex, value: data });
  }

  private updateCurrentPageClasses(index: number) {
    const currentPage: Element = document.querySelectorAll(`#${this.pdfID} > ${this.canvasClass}`)[index];
    if (!currentPage) {
      return;
    }
    this.currentVisiblePage?.classList.remove(this.currentPageClass);
    if (this.isPresentationTeacherMode) {
      this.currentVisiblePage?.classList.remove(this.currentPageTeacherClass);
    }
    const currentImage = currentPage.querySelector('.page-image');
    this.currentVisiblePage = currentImage;
    this.currentVisiblePage?.classList.add(this.currentPageClass);
    if (this.isPresentationTeacherMode && this.homeworksSideBar && this.zoomObject) {
      this.currentVisiblePage?.classList.add(this.currentPageTeacherClass);
    }
  }

  private setInitPagesInterval(): {startPage: number , endPage: number} {
    let startPage: number = this.initNumberPage ? this.initNumberPage : 1;
    let endPage: number = this.numberPages;
    if(startPage + this.numberRenderPages < this.numberPages) {
      endPage =  startPage + this.numberRenderPages - 1;
    }
    return {startPage: startPage  , endPage: endPage}
  }

  private async renderPage(page: any , pdfId : string) {
      let initialViewport = page.getViewport({ scale: 1 });
      let scale = (this.canvasViewport / initialViewport.width);
      let viewport: any = page.getViewport({ scale });
      let canvas: HTMLCanvasElement = document.createElement('canvas');
      this.canvasHeight = canvas.height = viewport.height;
      this.canvasWidth = canvas.width = viewport.width;

      // this.initPages[page._pageIndex].height = this.canvasHeight = canvas.height = viewport.height;
      // this.initPages[page._pageIndex].width = this.canvasWidth = canvas.width = viewport.width;
      // this.initPages[page._pageIndex].initPage = this.totalHeight;
      // this.totalHeight += this.initPages[page._pageIndex].height;

      let pageImage : HTMLImageElement = document.createElement('img');
      pageImage.classList.add('page-image');
      pageImage.classList.add(`pdf-img-${this.documentHash}`);
      pageImage.classList.add(this.documentHash);
      pageImage.width  = this.canvasWidth;
      pageImage.height = this.canvasHeight;
      pageImage.id = `pdf-img_${this.documentHash}_${page._pageIndex + 1}`;

      pageImage.addEventListener('load',
          this.generateImgLoadEventListenerForPageImage({
            pageIndex: page._pageIndex,
            width: initialViewport.width,
            height: initialViewport.height
          }));
      document.getElementById(pdfId)?.appendChild(canvas);
      document.getElementById(pdfId)?.appendChild(pageImage);

      let canvasContext = canvas.getContext('2d', {willReadFrequently: true});
      let renderContext = { canvasContext, viewport };

      //page.render() returns a RenderTask (https://github.com/mozilla/pdf.js/blob/master/src/display/api.js#L3158)
      return page.render(renderContext).promise.then(() => {
        pageImage.src = canvas.toDataURL();
        this.releaseCanvas(canvas);
        document.getElementById(pdfId)?.removeChild(canvas);
      });

  }

  /**
   * This method generates an event listener handler that creates a closure on a single parameter value, thus avoiding
   * referencing big objects that might cause nasty memory leaks.
   */
  private generateImgLoadEventListenerForPageImage(pageData: {
    pageIndex: number,
    width: number,
    height: number
  }): (Event) => void {
    return  (event: Event) => {
      const data = {
        height: pageData.height,
        width: pageData.width,
        page: pageData.pageIndex + 1,
        docKey: this.idRender,
        sectionType: SECTION_TYPE_CODES.LESSON_PDF
      };
      this.loadingPage(data);
      this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_ORIGINAL_PAGES_SIZE, {
        documentHash: this.documentHash,
        pageNumber  : pageData.pageIndex + 1,
        data
      });
      // Remove loading img
      const spinnerImg = document.getElementById(`spinnerImg_${this.documentHash}_${pageData.pageIndex + 1}`);
      if (spinnerImg) {
        spinnerImg.remove();
      }

      this.addPagesToRenderUp();

      //scroll document to the initial predefined page 
      if(this.initNumberPage == pageData.pageIndex + 1) {
        const pageImage = document.getElementById(`${(pageData.pageIndex + 1) + this.idRender}`);
        setTimeout(() => { pageImage.scrollIntoView(); });
      }

      if (this.currentSelectedPage == pageData.pageIndex + 1) {
        this.updateCurrentPageClasses(pageData.pageIndex);
      }
    };
  }



  @Emit('onInitRender')
  public onInitRender(): {id: number, width: number, height: number }[] {
    const pagesSizeInfo = this.initPages.map((page) => ({...page}));

    pagesSizeInfo.forEach((pageSize, index) => {
      if (this.zoomObject || this.isPresentationModeTaskBoard) {
        pageSize.initPage = index === 0 ? 0 : pagesSizeInfo[index - 1].initPage + pagesSizeInfo[index - 1].height;
      } else {
        pageSize.zoomInitPage = index === 0 ? 0 :
                            pagesSizeInfo[index - 1].zoomInitPage + pagesSizeInfo[index - 1].zoomHeight;
      }
    });
    return pagesSizeInfo;
  }

  @Emit('loadingPage')
  public loadingPage(data: {height: number, width: number, page: number, docKey: string}): void {
    // Let the @Emit decorator just emit the passed data
  }

  @Emit('onChangeSelectedPage')
  public onChangeSelectedPage(pageNumber: number): void {
    // Let the @Emit decorator just emit the passed data
  }


  private releaseCanvas(canvas: HTMLCanvasElement) {

    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx && ctx.clearRect(0, 0, 1, 1);
  }

  private async addPagesToRenderDown() {
    let startPage: number = this.numberRenderPages;
    let endPage: number = (startPage + this.addPages) > this.numberPages ?
        this.numberPages : (startPage + this.addPages - 1);

    if(this.numberPages > startPage) {
      await this.getPagesDown({startPage: startPage , endPage: endPage});
      let startLoop: number =  startPage + this.addPages > this.numberPages ?
      this.pages.length - (this.numberPages - startPage) - 1 :
      this.pages.length - this.addPages;
      let endLoop: number =  this.pages.length;

      for (let i = startLoop; i < endLoop; i++) {
        let pageId: number = this.initNumberPage ? ( this.numberRenderPages + (i-startLoop)) : (i + 1);
        let pdfId: string = String(pageId) + this.idRender;
        this.pageRenderingQueue.add(async () => await this.renderPage(this.pages[i], pdfId));
      }
      this.numberRenderPages += this.addPages
    }
    if(this.addPendingPages > 0){
      this.addPages = this.validatePageToAdd(this.addPendingPages);
      this.addPendingPages = 0;
      this.addPagesToRenderDown();
      return;
    }
    this.flagRender = true;
    this.addPages = 15;
    this.setCanvasLeft();
    this.changeCanvasSize();
  }

  private async getPagesDown(set: {startPage: number , endPage: number}) {
    for (let i = set.startPage; i <= set.endPage; i++) {
      let page =  await this.documentPDF.getPage(i);
      this.pages.push(page);
    }
  }

  private async addPagesToRenderUp() {
    let pageSizeZoomOut = this.getPageSizeZoomOut();
    let startLoop: number = this.initPage < this.addPages ? this.initPage - 1 : this.addPages;
    let startPage: number = this.initPage;
    let endPage: number =  startPage - this.addPages > 0 ?
    (startPage - this.addPages) : 1;
    await this.getPagesUp({startPage: startPage , endPage: endPage});
    for(let i = startLoop ; i >= 0 ; i--) {
      let pdfId: string = String(startPage - (startLoop - i)) + this.idRender;
      this.pageRenderingQueue.add(async () => await this.renderPage(this.pages[i], pdfId));
    }
    this.initPage = startPage - this.addPages - 1;
    this.flagRender = true;
    this.changeCanvasSize();
  }

  private async getPagesUp(set: {startPage: number , endPage: number}) {
    for (let i = set.startPage; i >= set.endPage; i--) {
      let page =  await this.documentPDF.getPage(i);
      this.pages.unshift(page);
    }
  }

  public async refresh() {
    this.pdfStatus.setLoading();
    this.$Courses.Store.setPdfLoading(true);
    this.$edEventBus.$emit(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash), true);
    try {
      await this.setPdf(this.url);
    } catch(err) {
      this.pdfStatus.setError();
      this.$Courses.Store.setPdfLoading(false);
      this.$edEventBus.$emit(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash), false);
    }
  }

  public setPageSize(page: any): string {
    let height: string = page.height > 0 ? page.height : this.canvasHeight;
    let width: string = page.width > 0 ? page.width : this.canvasWidth;

    if(this.isPresentationMode && this.isPresentationModeTaskBoard) {
      height = `${height}px`;
      width = `${width}px`;
    } else {
      height = this.zoomObject ? `${height}px` : '100%';
      width = this.zoomObject ? `${width}px` : '100%';
    }

    return `height: ${height}; width: ${width};`
  }

  public viewerResize(value) {
    if(!this.isResized && value.width > 0 && this.canvasViewport == 0) {
      this.isResized = true;
      this.renderPdf();
    }
  }

  public cleanProperties() {
    this.pages = [];
    this.initPages = [];
    this.numberPages = -1;
    this.numberRenderPages = 15;
    this.canvasViewport = 1000;
    this.canvasHeight = 0;
    this.canvasWidth = 0;
    this.flagRender = true;
    this.initPage = null;
  }

  public async renderPdf() {
    this.cleanProperties();
    setTimeout(() => { this.startRenderPDF(); }, 0);
    await ToolsService.sleep(500);
  }

  public async startRenderPDF() {
    try {
      if (this.courseId !== this.courseSelected && this.isPresentationTeacherMode && this.isSameUser) {
        return;
      }
      this.pdfStatus.setLoading();
      this.$Courses.Store.setPdfLoading(true);
      this.$edEventBus.$emit(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash), true);
      const containerToSelect: HTMLElement = this.pdfViewerElement;
      this.canvasViewport = containerToSelect.offsetWidth;
      this.initPage = this.initNumberPage;
      let lastScrollTop = containerToSelect.scrollTop;
      containerToSelect.addEventListener('scroll', () => {
        const scrollTop: number = containerToSelect.scrollTop;

        this.findCurrentPage(scrollTop);

        const isScrollingDown = scrollTop > lastScrollTop;
        const isScrollingUp = scrollTop < lastScrollTop;
        const needMorePagesDown = (this.currentSelectedPage + this.PAGE_LOT_TO_LOAD ) > this.numberRenderPages;
        const needMorePagesUp = (this.currentSelectedPage - this.PAGE_LOT_TO_LOAD) <= this.initPage;

        if (isScrollingDown && needMorePagesDown) {
          let pageToLoad: number;
          if (this.currentSelectedPage > this.numberRenderPages) {
            pageToLoad = Math.ceil((this.currentSelectedPage + this.PAGE_LOT_TO_LOAD - this.numberRenderPages)
                / this.PAGE_LOT_TO_LOAD) * this.PAGE_LOT_TO_LOAD;
          } else {
            pageToLoad = this.PAGE_LOT_TO_LOAD;
          }

          if (!this.flagRender && this.addPendingPages < pageToLoad) {
            this.addPendingPages = pageToLoad;
            return;
          } else if (this.flagRender) {
            pageToLoad = this.validatePageToAdd(pageToLoad);

            this.addPages = pageToLoad;
            this.flagRender = false;
            this.addPagesToRenderDown();
          }

        }

        if( isScrollingUp && needMorePagesUp && this.flagRender && this.initNumberPage) {
          this.flagRender = false;
          this.addPagesToRenderUp();
        }

        lastScrollTop = scrollTop;
      });
      await this.setPdf(this.url);
    } catch(err) {
      this.pdfStatus.setError();
      this.$Courses.Store.setPdfLoading(false);
      this.$edEventBus.$emit(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash), false);
    }
  }

  private validatePageToAdd(pageToLoad : number){
    return this.pages.length + pageToLoad > this.numberPages ? (this.numberPages - this.pages.length) + 1 : pageToLoad;
  }

  private getPageSizeZoomOut () {

    let docsContainer = document.getElementById('docs-container');
    let pageSizeZoomOut = docsContainer ? docsContainer.clientHeight : 0;

    if(document.getElementById('SolutionContainer')){
      pageSizeZoomOut = pageSizeZoomOut ? pageSizeZoomOut : document.getElementById('SolutionContainer')?.clientHeight;
    }
    if(document.getElementById('AnswerContainer')){
      pageSizeZoomOut = pageSizeZoomOut ? pageSizeZoomOut : document.getElementById('AnswerContainer')?.clientHeight;
    }
    return pageSizeZoomOut;
  }

  private changeCanvasSize() {    
    if(!this.isPresentationMode || this.isPresentationModeTaskBoard){
      return;
    }
    this.$nextTick(() => {
      let smallerPageWidth : number = null;
      let imageElements : HTMLCollectionOf<Element> = document.getElementsByClassName(`pdf-img-${this.documentHash}`);

      if(imageElements.length === 0){
        return;
      }
      let newTotalHeight = 0;
      imageElements['forEach']((element : any) => {
        if(smallerPageWidth === null || element.offsetWidth < smallerPageWidth){
          smallerPageWidth = element.offsetWidth;
        }
      });

      imageElements['forEach']((element : any, index : number) => {
        new Promise<void>((resolve, reject) => {
          if(!this.zoomObject && element.offsetWidth !== smallerPageWidth){
            let newHeight = (element.offsetHeight * smallerPageWidth) / element.offsetWidth;
            element.style.setProperty('width', `${smallerPageWidth}px`);
            element.style.setProperty('height', `${newHeight}px`);
            if(this.$refs[`containerCanvas${index+1}${this.idRender}`] && this.$refs[`containerCanvas${index+1}${this.idRender}`][0]){
              this.$refs[`containerCanvas${index+1}${this.idRender}`][0].style.setProperty('height', `${newHeight}px`, 'important');
            }
            resolve();
          }

          if(this.zoomObject){
            element.style.removeProperty('width');
            element.style.removeProperty('height');
            resolve();
          }

          if(!this.zoomObject){
            this.initPages[index].zoomInitPage = newTotalHeight;
            this.initPages[index].zoomHeight = element.offsetHeight;
            this.initPages[index].zoomWidth = element.offsetWidth;
            newTotalHeight += element.offsetHeight;
            resolve();
          }
        }).then(() => {
          this.$edEventBus.$emit(TRANSPARENCY_EVENTS.CHANGE_DOCUMENT_ZOOM, index + 1);
        });
      });

      if(!this.zoomObject){
        this.$refs[`pdfContainer${this.idRender}`]['style'].setProperty('width', `${smallerPageWidth}px`);
      } else {
        this.$refs[`pdfContainer${this.idRender}`]['style'].removeProperty('width');
      }

    });
  }

  private changeSplitTab() : void {
    if(this.zoomObject){
      return;
    }
    this.changeCanvasSize();
  }

  private changeResize({ homeworksSideBar }: {width: number, height: number, homeworksSideBar: boolean}){
    if(this.isPresentationTeacherMode){
      this.homeworksSideBar = homeworksSideBar;
      !homeworksSideBar || !this.zoomObject ? this.currentVisiblePage?.classList.remove(this.currentPageTeacherClass) : this.currentVisiblePage?.classList.add(this.currentPageTeacherClass);
    }
  }

  private updateCurrentPage() : void {
    if(this.isPresentationMode){
      setTimeout(() => {
        let pdfDocument : any = this.pdfViewerElement;
        this.findCurrentPage(pdfDocument.scrollTop)
      });
    }
  }

  private setCanvasLeft() {
    const canvas = [...document.getElementsByTagName('canvas')];
    if(this.zoomObject) {
      canvas.forEach(el => el.style.left = '0');
    } else {
      canvas.forEach(el => el.removeAttribute('style'));
    }
  }

  private renderAddedPages(addedPages: BoardPage[]) {
    if (addedPages.length == 0) {
      return;
    }
    // Filter out pages that are already in initPages
    addedPages = addedPages.filter(page => !this.initPages.find(initPage => initPage.id == page.page));

    // Add extra pages
    if (addedPages.length > 0) {
      addedPages.forEach(page => {
        const scale = (this.canvasViewport / page.width);
        const width = page.width * scale;
        const height = page.height * scale;

        this.initPages.push({
          id      : page.page,
          height,
          width,
        });
        this.recalculatePageSizes();
      });

    }
    this.$nextTick(() => {
      addedPages.forEach(page => this.renderAddedPage(page));
    });

  }

  private renderAddedPage(page: BoardPage): void {
    let pageImage : HTMLImageElement = document.createElement('img');
    pageImage.classList.add('page-image');
    pageImage.classList.add(`pdf-img-${this.documentHash}`);
    pageImage.classList.add(this.idRender);
    pageImage.width  = this.canvasWidth;
    pageImage.height = this.canvasHeight;
    pageImage.id = `pdf-img_${this.documentHash}_${page.page}`;

    pageImage.src = page.backgroundImageUrl || BLANK_PIXEL_IMG;

    pageImage.addEventListener('load',
        this.generateImgLoadEventListenerForPageImage({
          pageIndex: page.page - 1,
          width: page.width,
          height: page.height
        }));

    const imageContainer = document.getElementById(`${page.page}${this.idRender}`);
    imageContainer?.removeChild(imageContainer.firstChild); // Remove loading spinner
    imageContainer?.appendChild(pageImage);
  }

  private recalculatePageSizes() {
    // Sort initPages by page number
    this.initPages.sort((a, b) => a.id - b.id);

    // Accumulate total height and set it as the initPage property of each element
    this.totalHeight = 0;
    this.initPages.forEach(page => {
        page.initPage = this.totalHeight;
        this.totalHeight += page.height;
    });
  }

  private disposePDF() {
    try {
      this.pdfDocLoadingTask?.destroy();
    } catch {
      this.pdfDocLoadingTask = null;
    }

    try {
      this.documentPDF?.destroy();
    } catch {
      this.documentPDF = null;
    }

    try {
      this.pages.forEach(page => page.cleanUp());
    } catch {
      this.pages = null;
    }

    this.pdfDocLoadingTask = null;
    this.documentPDF = null;
    this.pages = null;
  }
  /* Hooks */

  mounted() {
    this.$edEventBus.$on(MEETING_EVENTS.CHANGE_SPLIT_TAB, this.changeSplitTab);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_RESIZE, this.changeResize);
    this.$edEventBus.$on(COURSE_EVENTS.SEND_PRESENTATION_LAYOUT, this.updateCurrentPage);
    this.$edEventBus.$on(`${PRESENTATION_COURSE_TAB_EVENT}${this.courseId}`, this.updateCurrentPage);
    
    if (this.allowExtraPages) {
      this.$edEventBus.$on(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, this.renderAddedPages);
      this.$edEventBus.$on(`${BOARD_EVENTS.BOARD_ACTIVATED}_${this.documentHash}`, this.defineCurrentPage);
    }
  }


  beforeDestroy() {
    this.$edEventBus.$off(MEETING_EVENTS.CHANGE_SPLIT_TAB, this.changeSplitTab);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_RESIZE, this.changeResize);
    this.$edEventBus.$off(COURSE_EVENTS.SEND_PRESENTATION_LAYOUT, this.updateCurrentPage);
    this.$edEventBus.$off(`${PRESENTATION_COURSE_TAB_EVENT}${this.courseId}`, this.updateCurrentPage);
    
    if (this.allowExtraPages) {
      this.$edEventBus.$off(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, this.renderAddedPages);
      this.$edEventBus.$off(`${BOARD_EVENTS.BOARD_ACTIVATED}_${this.documentHash}`, this.defineCurrentPage);
    }
    
    this.$store.commit( _MEETINGS_STORE.MUTATIONS.CLEAN_ORIGINAL_PAGES_SIZE ,{ 'documentHash': this.documentHash});
    this.disposePDF();
  }

  @Watch('zoomObject')
  updateCanvasHeight() {
    if(!this.isPresentationModeTaskBoard) {
      let pageSizeZoomIn = this.canvasHeight;
      let pageSizeZoomOut = this.getPageSizeZoomOut();
      this.pageSize = this.zoomObject ? pageSizeZoomIn : pageSizeZoomOut;
      let containerToZoom = this.pdfViewerElement;
      this.changeCanvasSize();
      if(!containerToZoom){
        return;
      }
      let scrollZoomIn = pageSizeZoomIn * containerToZoom.scrollTop / pageSizeZoomOut;
      let scrollZoomOut = pageSizeZoomOut * containerToZoom.scrollTop / pageSizeZoomIn;
      setTimeout(() => {
        containerToZoom.scrollTop = this.zoomObject ? scrollZoomIn : scrollZoomOut;
        this.findCurrentPage(containerToZoom.scrollTop);
        this.$edEventBus.$emit(MEETING_EVENTS.SCROLL_WHITEBOARD_TRANSPARENCY);
        this.$edEventBus.$emit(TRANSPARENCY_EVENTS.SCROLL_EVENT);
      }, 0);
      this.setCanvasLeft();
    }
  }

}
